import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ideaService } from '../../../services/ideas.service';
import { eventService } from '../../../services/event.service';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import MultiSelectAutocomplete from '../../UI/MultiSelectAutocomplete/MultiSelectAutocomplete';
import Alert from '../../UI/Alert/Alert';
import styles from './IdeasCreateForm.module.css';
import { toast } from 'react-toastify';

function IdeasCreateForm({ isOpen, onClose, onSuccess, tagsList = [], statuses = [] }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const fileInputRef = useRef(null);

    const [activeEvents, setActiveEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState({ value: '', error: null });
    const [eventsLoading, setEventsLoading] = useState(true);

    const [title, setTitle] = useState({ value: '', error: null });
    const [description, setDescription] = useState({ value: '', error: null });
    const [tags, setTags] = useState({ value: [], error: null });
    const [status, setStatus] = useState({ value: 'ready_to_review', error: null });
    const [file, setFile] = useState({ value: null, error: null });
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const loadEvents = async () => {
            setEventsLoading(true);
            try {
                const data = await eventService.getActiveEvents();
                setActiveEvents(data?.events ?? data ?? []);
            } catch {
                setActiveEvents([]);
            } finally {
                setEventsLoading(false);
            }
        };
        loadEvents();
    }, [isOpen]);

    const resetForm = () => {
        setTitle({ value: '', error: null });
        setDescription({ value: '', error: null });
        setTags({ value: [], error: null });
        setStatus({ value: '', error: null });
        setFile({ value: null, error: null });
        setSelectedEventId({ value: '', error: null });
        setGeneralError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setTitle({ value: title.value, error: null });
        setDescription({ value: description.value, error: null });
        setTags({ value: tags.value, error: null });
        setStatus({ value: status.value, error: null });
        setFile({ value: file.value, error: null });
        setGeneralError(null);

        try {
            const tagsArray = tags.value.map((tag) => tag?.code ?? tag?.value ?? tag);

            const formData = new FormData();
            formData.append('title', title.value);
            formData.append('description', description.value);
            formData.append('tags', JSON.stringify(tagsArray));
            formData.append('status', status.value);
            if (file.value) {
                formData.append('file', file.value);
            }
            if (selectedEventId.value) {
                formData.append('event_id', selectedEventId.value);
            }

            const response = await ideaService.createIdea(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.status === 'success' || response.idea) {
                resetForm();
                toast.success(tValidation('idea_created_successfully'));
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach((errorItem) => {
                    const field = errorItem.path?.[0];
                    if (field === 'title') {
                        setTitle({ value: title.value, error: errorItem });
                    }
                    if (field === 'description') {
                        setDescription({ value: description.value, error: errorItem });
                    }
                    if (field === 'tags') {
                        setTags({ value: tags.value, error: errorItem });
                    }
                    if (field === 'status') {
                        setStatus({ value: status.value, error: errorItem });
                    }
                    if (field === 'file') {
                        setFile({ value: file.value, error: errorItem });
                    }
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity.toString());
                setGeneralError(message);
                toast.error(message);
            } else {
                toast.error(tValidation('something_went_wrong'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            resetForm();
            onClose();
        }
    };

    const fileButtonText = t('ideas.file_button');
    const filePlaceholder = t('ideas.file_placeholder');


    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{t('ideas.create')}</h2>
                    <p className={styles.subtitle}>{t('ideas.create_subtitle')}</p>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={isLoading}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {generalError && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}
                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('events.event_select_label')}</label>
                        {eventsLoading ? (
                            <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: 14 }}>{t('loading', 'Loading...')}</p>
                        ) : (
                            <Select
                                options={[
                                    { value: '', label: t('events.event_no_event') },
                                    ...activeEvents.map((ev) => {
                                        const title = ev.title.length > 35 ? ev.title.slice(0, 35) + '…' : ev.title;
                                        const date = new Date(ev.end_date).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' });
                                        return { value: String(ev.id), label: `${title} — до ${date}` };
                                    }),
                                ]}
                                value={selectedEventId.value}
                                onChange={(e) => setSelectedEventId({ value: e.target.value, error: null })}
                                error={selectedEventId.error}
                            />
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.title_label')}</label>
                        <Input
                            type="text"
                            placeholder={t('ideas.title_placeholder')}
                            value={title.value}
                            onChange={(e) => {
                                const val = e.target.value;
                                let error = null;
                                if (val.length > 100) {
                                    error = { code: 'title_too_long', params: { maximum: 100 } };
                                }
                                setTitle({ value: val, error });
                            }}
                            error={title.error}
                            required
                        />
                        <div className={`${styles.charCount} ${title.value.length > 100 ? styles.charCountError : ''}`}>
                            {title.value.length} / 100
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.description_label')}</label>
                        <textarea
                            className={`${styles.textarea} ${description.error ? styles.textareaError : ''}`}
                            placeholder={t('ideas.description_placeholder')}
                            value={description.value}
                            onChange={(e) => {
                                const val = e.target.value;
                                let error = null;
                                if (val.length > 500) {
                                    error = { code: 'description_too_long', params: { maximum: 500 } };
                                }
                                setDescription({ value: val, error });
                            }}
                            rows={4}
                            required
                        />
                        <div className={`${styles.charCount} ${description.value.length > 500 ? styles.charCountError : ''}`}>
                            {description.value.length} / 500
                        </div>
                        {description.error && (
                            <p className={styles.errorText}>
                                {
                                    tValidation(description.error?.code || '').toString().replace('{{toReplace}}', description.error?.params?.maximum || description.error?.params?.min)
                                }
                            </p>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.tags_label')}</label>
                        <MultiSelectAutocomplete
                            options={tagsList}
                            value={tags.value}
                            onChange={(values) => setTags({ value: values, error: null })}
                            placeholder={t('ideas.tags_placeholder')}
                            error={tags.error}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.status_label')}</label>
                        <Select
                            options={statuses}
                            value={status.value}
                            onChange={(e) => setStatus({ value: e.target.value, error: null })}
                            placeholder={t('ideas.status')}
                            error={status.error}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.file_label')}</label>
                        <div className={styles.fileInputWrapper}>
                            <input
                                ref={fileInputRef}
                                id="idea-file"
                                type="file"
                                className={styles.fileInputHidden}
                                onChange={(e) => setFile({ value: e.target.files?.[0] ?? null, error: null })}
                                accept=".pdf"
                            />
                            <span className={styles.fileName}>
                                {file.value?.name || filePlaceholder}
                            </span>
                            <label className={styles.fileButton} htmlFor="idea-file">
                                {fileButtonText}
                            </label>
                        </div>
                        {file.error && (
                            <p className={styles.errorText}>
                                {tValidation(file.error?.code || '')}
                            </p>
                        )}
                    </div>

                    <div className={styles.buttons}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            {t('ideas.cancel')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={isLoading || !!description.error || !!title.error || !!file.error}>
                            {t('ideas.create')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default IdeasCreateForm;
